import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Plus, Minus, Trash2, MapPin, Phone, User, Mail, MessageSquare, Check, ChevronDown, ChevronUp, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labels = {
  fr: {
    title: 'Livraison', subtitle: 'Paiement à la livraison',
    yourOrder: 'Votre commande', emptyCart: 'Panier vide',
    itemNote: 'Note pour ce plat (ex: sans oignon, à point...)',
    addNote: '+ Note',
    subtotal: 'Sous-total', deliveryFee: 'Frais de livraison', free: 'Gratuit',
    freeAt: 'Livraison gratuite à partir de',
    minOrder: 'Commande minimum',
    addMore: 'Ajoutez',
    forFreeDelivery: 'pour la livraison gratuite',
    minOrderNotMet: 'Commande minimum non atteinte',
    total: 'Total', orderBtn: 'Commander', back: 'Retour', confirm: 'Confirmer',
    name: 'Votre nom *', email: 'Email * (pour suivi de commande)', phone: 'Téléphone *',
    address: 'Adresse de livraison *', notes: 'Notes (allergies, instructions...)',
    invalidEmail: 'Veuillez entrer un email valide',
    successTitle: 'Commande envoyée !', successThanks: 'Merci',
    successContact: 'Nous vous contacterons au', successConfirm: 'pour confirmer votre commande. Paiement à la livraison.',
    newOrder: 'Nouvelle commande',
    fillRequired: 'Veuillez remplir tous les champs obligatoires',
    cartEmpty: 'Votre panier est vide', errorOrder: 'Erreur lors de la commande',
    unavailable: 'La livraison n\'est pas disponible actuellement',
    closedTitle: 'Livraison fermée', closedDay: 'La livraison est fermée aujourd\'hui',
    closedHours: 'La livraison est fermée à cette heure',
    nextSlot: 'Prochain créneau de livraison :', hoursLabel: 'Horaires de livraison',
    deliveryDisabled: 'La livraison est actuellement désactivée.',
    backHome: 'Retour à l\'accueil'
  },
  en: {
    title: 'Delivery', subtitle: 'Pay on delivery',
    yourOrder: 'Your order', emptyCart: 'Empty cart',
    itemNote: 'Note for this item (e.g. no onion, well done...)',
    addNote: '+ Note',
    subtotal: 'Subtotal', deliveryFee: 'Delivery fee', free: 'Free',
    freeAt: 'Free delivery from',
    minOrder: 'Minimum order',
    addMore: 'Add',
    forFreeDelivery: 'for free delivery',
    minOrderNotMet: 'Minimum order not reached',
    total: 'Total', orderBtn: 'Order', back: 'Back', confirm: 'Confirm',
    name: 'Your name *', email: 'Email * (for order tracking)', phone: 'Phone *',
    address: 'Delivery address *', notes: 'Notes (allergies, instructions...)',
    invalidEmail: 'Please enter a valid email',
    successTitle: 'Order sent!', successThanks: 'Thank you',
    successContact: 'We will contact you at', successConfirm: 'to confirm your order. Pay on delivery.',
    newOrder: 'New order',
    fillRequired: 'Please fill all required fields',
    cartEmpty: 'Your cart is empty', errorOrder: 'Error placing order',
    unavailable: 'Delivery is currently unavailable',
    closedTitle: 'Delivery closed', closedDay: 'Delivery is closed today',
    closedHours: 'Delivery is closed at this hour',
    nextSlot: 'Next delivery slot:', hoursLabel: 'Delivery hours',
    deliveryDisabled: 'Delivery is currently disabled.',
    backHome: 'Back to home'
  },
  pt: {
    title: 'Entrega', subtitle: 'Pagamento na entrega',
    yourOrder: 'O seu pedido', emptyCart: 'Carrinho vazio',
    itemNote: 'Nota para este prato (ex: sem cebola, bem passado...)',
    addNote: '+ Nota',
    subtotal: 'Subtotal', deliveryFee: 'Taxa de entrega', free: 'Grátis',
    freeAt: 'Entrega grátis a partir de',
    minOrder: 'Pedido mínimo',
    addMore: 'Adicione',
    forFreeDelivery: 'para entrega grátis',
    minOrderNotMet: 'Pedido mínimo não atingido',
    total: 'Total', orderBtn: 'Encomendar', back: 'Voltar', confirm: 'Confirmar',
    name: 'O seu nome *', email: 'Email * (para acompanhar o pedido)', phone: 'Telefone *',
    address: 'Endereço de entrega *', notes: 'Notas (alergias, instruções...)',
    invalidEmail: 'Por favor introduza um email válido',
    successTitle: 'Pedido enviado!', successThanks: 'Obrigado',
    successContact: 'Iremos contactá-lo em', successConfirm: 'para confirmar o seu pedido. Pagamento na entrega.',
    newOrder: 'Novo pedido',
    fillRequired: 'Preencha todos os campos obrigatórios',
    cartEmpty: 'O seu carrinho está vazio', errorOrder: 'Erro ao encomendar',
    unavailable: 'A entrega não está disponível neste momento',
    closedTitle: 'Entrega fechada', closedDay: 'A entrega está fechada hoje',
    closedHours: 'A entrega está fechada nesta hora',
    nextSlot: 'Próximo horário de entrega:', hoursLabel: 'Horários de entrega',
    deliveryDisabled: 'A entrega está atualmente desativada.',
    backHome: 'Voltar ao início'
  }
};

const DeliveryPage = () => {
  const { language, getLocalizedField } = useLanguage();
  const L = labels[language] || labels.fr;
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [expandedCat, setExpandedCat] = useState(null);
  const [step, setStep] = useState('menu'); // menu, checkout, success
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', phone: '', address: '', notes: '' });
  const [deliveryStatus, setDeliveryStatus] = useState(null); // {enabled, is_open, reason, time_slots, ...}
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [itemsRes, catsRes, statusRes] = await Promise.all([
          axios.get(`${API}/menu-items`),
          axios.get(`${API}/menu-categories`),
          axios.get(`${API}/delivery/status`)
        ]);
        setMenuItems(itemsRes.data || []);
        setCategories(catsRes.data || []);
        setDeliveryStatus(statusRes.data || { enabled: false, is_open: false });
        if (catsRes.data?.length > 0) setExpandedCat(catsRes.data[0].id);
      } catch (e) {
        console.error(e);
      } finally {
        setStatusLoading(false);
      }
    };
    fetchAll();
  }, []);

  const addToCart = useCallback((item) => {
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.id);
      if (existing) return prev.map(c => c.item_id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { item_id: item.id, name_fr: item.name_fr, price: item.price, quantity: 1, notes: '' }];
    });
  }, []);

  const updateItemNotes = (itemId, notes) => {
    setCart(prev => prev.map(c => c.item_id === itemId ? { ...c, notes } : c));
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => prev.map(c => {
      if (c.item_id !== itemId) return c;
      const newQty = c.quantity + delta;
      return newQty <= 0 ? null : { ...c, quantity: newQty };
    }).filter(Boolean));
  };

  const subtotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const baseFee = deliveryStatus?.delivery_fee || 0;
  const freeThreshold = deliveryStatus?.delivery_free_threshold || 0;
  const minOrder = deliveryStatus?.delivery_min_order || 0;
  const fee = (freeThreshold > 0 && subtotal >= freeThreshold) ? 0 : baseFee;
  const total = subtotal + fee;
  const minNotMet = minOrder > 0 && subtotal < minOrder && cart.length > 0;
  const amountForFree = freeThreshold > 0 && subtotal > 0 && subtotal < freeThreshold ? freeThreshold - subtotal : 0;

  const handleSubmit = async () => {
    if (!form.customer_name || !form.phone || !form.address || !form.customer_email) {
      toast.error(L.fillRequired);
      return;
    }
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)) {
      toast.error(L.invalidEmail);
      return;
    }
    if (cart.length === 0) { toast.error(L.cartEmpty); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/delivery/orders`, { ...form, items: cart });
      setStep('success');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Minimum')) {
        toast.error(L.minOrder + ': ' + error.response.data.detail.replace('Minimum order is ', ''));
      } else if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('today')) toast.error(L.closedDay);
        else if (detail.includes('hour')) toast.error(L.closedHours);
        else toast.error(L.unavailable);
      } else toast.error(L.errorOrder);
    } finally { setLoading(false); }
  };

  // Loading state
  if (statusLoading) {
    return (
      <main className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center px-4">
        <p className="text-white/50">...</p>
      </main>
    );
  }

  // Delivery globally disabled
  if (!deliveryStatus?.enabled) {
    return (
      <main className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
          <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-white/40" />
          </div>
          <h2 className="font-display text-3xl text-white mb-3">{L.title}</h2>
          <p className="text-white/50 text-sm mb-8">{L.deliveryDisabled}</p>
          <a href="/" className="inline-block text-[#d4af37] text-xs uppercase tracking-[0.2em] hover:text-white transition" data-testid="delivery-back-home">{L.backHome}</a>
        </motion.div>
      </main>
    );
  }

  const deliveryMessage = deliveryStatus[`message_${language}`] || deliveryStatus.message_fr || '';

  if (step === 'success') {
    return (
      <main className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md" data-testid="delivery-success">
          <div className="w-20 h-20 bg-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-black" />
          </div>
          <h2 className="font-display text-3xl text-white mb-4">{L.successTitle}</h2>
          <p className="text-white/60 mb-2">{L.successThanks}, {form.customer_name} !</p>
          <p className="text-white/50 text-sm mb-6">{L.successContact} {form.phone} {L.successConfirm}</p>
          <p className="text-[#d4af37] text-xl font-bold mb-8">{L.total} : {total.toFixed(2)}€</p>
          <Button data-testid="delivery-new-order-btn" onClick={() => { setStep('menu'); setCart([]); setForm({ customer_name: '', customer_email: '', phone: '', address: '', notes: '' }); }} className="bg-white/10 text-white hover:bg-white/20">
            {L.newOrder}
          </Button>
        </motion.div>
      </main>
    );
  }

  const isClosed = !deliveryStatus.is_open;

  return (
    <main className="min-h-screen bg-[#0a0a0a] pt-20 pb-32" data-testid="delivery-page">
      {/* Header */}
      <section className="py-12 px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-3">BÉNI Restaurant</p>
          <h1 className="font-display text-4xl md:text-5xl text-white mb-2">{L.title}</h1>
          <p className="text-white/50 text-sm">{L.subtitle}</p>
        </motion.div>
      </section>

      {/* Closed banner */}
      {isClosed && (
        <div className="max-w-5xl mx-auto px-4 md:px-6 mb-6">
          <div className="bg-red-500/10 border border-red-500/40 p-4 rounded flex items-start gap-3" data-testid="delivery-closed-banner">
            <Clock className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium text-sm">
                {deliveryStatus.reason === 'closed_day' ? L.closedDay : L.closedHours}
              </p>
              {deliveryMessage && (
                <p className="text-white/60 text-xs mt-1">{deliveryMessage}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hours info (when open) */}
      {!isClosed && (deliveryMessage || baseFee > 0 || freeThreshold > 0 || minOrder > 0) && (
        <div className="max-w-5xl mx-auto px-4 md:px-6 mb-6">
          <div className="bg-[#d4af37]/10 border border-[#d4af37]/30 p-3 rounded space-y-1" data-testid="delivery-info-banner">
            {deliveryMessage && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#d4af37] flex-shrink-0" />
                <p className="text-white/70 text-xs">{deliveryMessage}</p>
              </div>
            )}
            {(baseFee > 0 || freeThreshold > 0 || minOrder > 0) && (
              <div className="flex items-center gap-3 flex-wrap pl-7 text-xs">
                {baseFee > 0 && <span className="text-white/60">• {L.deliveryFee}: <span className="text-[#d4af37]">{baseFee.toFixed(2)}€</span></span>}
                {freeThreshold > 0 && <span className="text-white/60">• {L.freeAt} <span className="text-green-400">{freeThreshold.toFixed(2)}€</span></span>}
                {minOrder > 0 && <span className="text-white/60">• {L.minOrder}: <span className="text-white/80">{minOrder.toFixed(2)}€</span></span>}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Menu */}
        <div className="lg:col-span-2 space-y-2">
          {categories.filter(cat => cat.delivery_available !== false).map(cat => {
            const items = menuItems.filter(i => i.category === cat.slug && i.delivery_available !== false && i.is_available !== false);
            if (items.length === 0) return null;
            const isExpanded = expandedCat === cat.id;
            const catName = getLocalizedField(cat, 'name');
            return (
              <div key={cat.id} className="border border-white/10 bg-[#121212]">
                <button onClick={() => setExpandedCat(isExpanded ? null : cat.id)} className="w-full flex items-center justify-between p-4 text-white hover:bg-white/5 transition" data-testid={`delivery-cat-${cat.slug}`}>
                  <span className="font-medium text-sm">{catName}</span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-[#d4af37]" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                      <div className="px-4 pb-3 space-y-1">
                        {items.map(item => {
                          const inCart = cart.find(c => c.item_id === item.id);
                          const itemName = getLocalizedField(item, 'name');
                          const itemDesc = getLocalizedField(item, 'description');
                          return (
                            <div key={item.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                              <div className="flex-1 min-w-0">
                                <p className="text-white text-sm">{itemName}</p>
                                {itemDesc && <p className="text-white/40 text-xs truncate">{itemDesc}</p>}
                                <p className="text-[#d4af37] text-xs font-medium">{item.price.toFixed(2)}€</p>
                              </div>
                              {inCart ? (
                                <div className="flex items-center gap-2 ml-3">
                                  <button onClick={() => updateQuantity(item.id, -1)} className="w-6 h-6 rounded bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid={`delivery-decrease-${item.id}`}>
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <span className="text-white text-sm w-5 text-center">{inCart.quantity}</span>
                                  <button onClick={() => updateQuantity(item.id, 1)} className="w-6 h-6 rounded bg-[#d4af37] text-black flex items-center justify-center hover:bg-white" data-testid={`delivery-increase-${item.id}`}>
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => addToCart(item)}
                                  disabled={isClosed}
                                  className="ml-3 w-7 h-7 rounded bg-white/10 text-[#d4af37] flex items-center justify-center hover:bg-[#d4af37] hover:text-black transition disabled:opacity-30 disabled:cursor-not-allowed"
                                  data-testid={`delivery-add-${item.id}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* Cart / Checkout */}
        <div className="lg:col-span-1">
          <div className="bg-[#121212] border border-white/10 p-4 sticky top-24 space-y-4" data-testid="delivery-cart">
            <h3 className="text-white font-medium flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-[#d4af37]" />
              {L.yourOrder} {cart.length > 0 && <span className="text-[#d4af37] text-xs">({cart.reduce((s, c) => s + c.quantity, 0)})</span>}
            </h3>

            {cart.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-6">{L.emptyCart}</p>
            ) : (
              <>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cart.map(c => (
                    <div key={c.item_id} className="space-y-1 pb-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex-1 min-w-0">
                          <span className="text-white truncate block">{c.quantity}x {c.name_fr}</span>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-white/60 text-xs">{(c.price * c.quantity).toFixed(2)}€</span>
                          <button onClick={() => setCart(prev => prev.filter(i => i.item_id !== c.item_id))} className="text-red-400/60 hover:text-red-400" data-testid={`delivery-remove-${c.item_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {/* Per-item note */}
                      <Input
                        value={c.notes || ''}
                        onChange={(e) => updateItemNotes(c.item_id, e.target.value)}
                        className="bg-black/30 border-white/10 text-white text-[11px] h-7 placeholder:text-white/30"
                        placeholder={L.itemNote}
                        data-testid={`delivery-item-note-${c.item_id}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-3 space-y-1.5">
                  {(baseFee > 0 || freeThreshold > 0) && (
                    <>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-white/50">{L.subtotal}</span>
                        <span className="text-white/70">{subtotal.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-white/50">{L.deliveryFee}</span>
                        {fee === 0 ? (
                          <span className="text-green-400 font-medium">{L.free}</span>
                        ) : (
                          <span className="text-white/70">{fee.toFixed(2)}€</span>
                        )}
                      </div>
                    </>
                  )}
                  {amountForFree > 0 && (
                    <p className="text-[10px] text-[#d4af37]/80 italic" data-testid="delivery-free-hint">
                      {L.addMore} {amountForFree.toFixed(2)}€ {L.forFreeDelivery}
                    </p>
                  )}
                  {minNotMet && (
                    <p className="text-[10px] text-red-400 italic" data-testid="delivery-min-warning">
                      {L.minOrder}: {minOrder.toFixed(2)}€ ({L.minOrderNotMet})
                    </p>
                  )}
                  <div className="flex justify-between items-center pt-1.5 border-t border-white/5">
                    <span className="text-white/60 text-sm">{L.total}</span>
                    <span className="text-[#d4af37] font-bold text-lg" data-testid="delivery-total">{total.toFixed(2)}€</span>
                  </div>
                </div>

                {step === 'menu' ? (
                  <Button
                    onClick={() => setStep('checkout')}
                    disabled={isClosed || minNotMet}
                    className="w-full bg-[#d4af37] text-black hover:bg-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="delivery-order-btn"
                  >
                    {L.orderBtn}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#d4af37]" />
                      <Input value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} className="bg-transparent border-white/20 text-white text-sm h-9 pl-9" placeholder={L.name} data-testid="delivery-input-name" />
                    </div>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#d4af37]" />
                      <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-transparent border-white/20 text-white text-sm h-9 pl-9" placeholder={L.phone} data-testid="delivery-input-phone" />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                      <Input type="email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} className="bg-transparent border-white/20 text-white text-sm h-9 pl-9" placeholder={L.email} data-testid="delivery-input-email" />
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-3.5 h-3.5 text-[#d4af37]" />
                      <Textarea value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="bg-transparent border-white/20 text-white text-sm pl-9 resize-none" rows={2} placeholder={L.address} data-testid="delivery-input-address" />
                    </div>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-3.5 h-3.5 text-white/30" />
                      <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-transparent border-white/20 text-white text-sm pl-9 resize-none" rows={2} placeholder={L.notes} data-testid="delivery-input-notes" />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setStep('menu')} className="border-white/20 text-white/50 text-sm flex-1" data-testid="delivery-back-btn">{L.back}</Button>
                      <Button onClick={handleSubmit} disabled={loading || isClosed || minNotMet} className="bg-[#d4af37] text-black hover:bg-white font-medium text-sm flex-1" data-testid="delivery-confirm-btn">
                        {loading ? '...' : L.confirm}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default DeliveryPage;
